/**
 * Ordered member rendering for CMS article blocks.
 *
 * `bodyHtml` remains the legacy projection for articles whose published
 * revision predates block authoring. When blocks exist, this component keeps
 * authored prose and accordions in their saved order instead of rebuilding a
 * second presentation from `bodyHtml` and metadata.
 */

import { type JSX, useEffect, useRef } from 'react';

import type { ArticleContentBlock } from '../ContentReaderPort.js';
import { SanitizedHtml } from '../components/SanitizedHtml.js';
import { bodyClasses } from './articleDetailSurface.js';

const blocksClasses = 'flex flex-col gap-3';
const accordionClasses = 'rounded-md border border-border px-4 py-3';
const accordionSummaryClasses = 'cursor-pointer select-none text-base font-medium text-foreground';
const accordionBodyClasses = 'pt-3 text-base text-foreground';

export interface ArticleContentBlocksProps {
  readonly contentBlocks: readonly ArticleContentBlock[];
  /**
   * Route-owned behavior for authored contact intent. The legacy prototype
   * encodes it as `/contact`, which is not a member route in this app.
   */
  readonly onContactIntent?: () => void;
}

function blockKey(block: ArticleContentBlock): string {
  return block.kind === 'text'
    ? `text:${block.html}`
    : `accordion:${block.title}:${block.bodyHtml}`;
}

/**
 * Replaces the unsupported prototype route with an in-page intent marker
 * before sanitisation. The visible link copy survives verbatim; the route
 * owns the actual behavior through `onContactIntent`.
 */
function markContactIntent(html: string): string {
  return html.replace(
    /\bhref\s*=\s*(?:"\/contact"|'\/contact'|\/contact(?=[\s>]))/giu,
    'href="#contact" data-contact-intent="true"',
  );
}

interface TextBlockProps {
  readonly html: string;
  readonly onContactIntent?: (() => void) | undefined;
}

/**
 * The body is opaque to React after sanitisation, so the contact marker needs
 * narrowly-scoped DOM listeners. They are attached only to marked anchors
 * and cleaned up whenever the authored block or route action changes.
 */
function TextBlock({ html, onContactIntent }: TextBlockProps): JSX.Element {
  const ref = useRef<HTMLElement>(null);
  const renderedHtml = onContactIntent === undefined ? html : markContactIntent(html);

  useEffect(() => {
    if (onContactIntent === undefined || !renderedHtml.includes('data-contact-intent')) return;
    const contactLinks = Array.from(
      ref.current?.querySelectorAll<HTMLAnchorElement>('a[data-contact-intent="true"]') ?? [],
    );

    const onContactClick = (event: MouseEvent): void => {
      event.preventDefault();
      onContactIntent();
    };
    for (const link of contactLinks) link.addEventListener('click', onContactClick);
    return () => {
      for (const link of contactLinks) link.removeEventListener('click', onContactClick);
    };
  }, [onContactIntent, renderedHtml]);

  return <SanitizedHtml ref={ref} as="div" className={bodyClasses} html={renderedHtml} />;
}

/**
 * Renders text and accordion blocks in their authored order. Native details
 * and summary elements provide keyboard-accessible disclosure behavior without
 * local React state.
 */
export function ArticleContentBlocks({
  contentBlocks,
  onContactIntent,
}: ArticleContentBlocksProps): JSX.Element {
  const occurrences = new Map<string, number>();
  const keyedBlocks = contentBlocks.map((block) => {
    const base = blockKey(block);
    const occurrence = occurrences.get(base) ?? 0;
    occurrences.set(base, occurrence + 1);
    return { block, key: `${base}:${occurrence}` };
  });
  return (
    <div className={blocksClasses}>
      {keyedBlocks.map(({ block, key }) =>
        block.kind === 'text' ? (
          <TextBlock key={key} html={block.html} onContactIntent={onContactIntent} />
        ) : (
          <details key={key} className={accordionClasses}>
            <summary className={accordionSummaryClasses}>{block.title}</summary>
            <SanitizedHtml as="div" className={accordionBodyClasses} html={block.bodyHtml} />
          </details>
        ),
      )}
    </div>
  );
}
